// Pre-cutover view model, retained for history-off Threads during the rollout.
import type {AppError} from '../../core/types';
import type {
  ControllerError,
  ControllerResult,
  ConversationOverview,
  ListConversationFilters,
} from '../conversations/types';
import {isConversationOverviewEnded} from './LegacyConversationViewModel';
import type {LegacyConversationsController} from '../app/types';
import {ThreadsViewModel} from './ThreadsViewModel';

export const DEFAULT_HISTORY_LIMIT = 20;

export type HistoryViewModelState = {
  isOpen: boolean;
  overviews: ConversationOverview[];
  loading: boolean;
  error: AppError | ControllerError | null;
  currentConversationId: string;
};

type HistoryViewModelOptions = {
  conversations: LegacyConversationsController;
  getCurrentConversationId: () => string;
  getCurrentOverview: () => ConversationOverview | null;
  selectConversation: (id: string) => void;
};

const unknownError = (error: unknown): ControllerError => ({
  type: 'error',
  error: error instanceof Error ? error.message : 'Unknown history error',
});

const sortByLastUpdatedDesc = (
  overviews: ConversationOverview[],
): ConversationOverview[] =>
  [...overviews].sort((a, b) => b.last_updated_at - a.last_updated_at);

export class HistoryViewModel extends ThreadsViewModel<HistoryViewModelState> {
  private _conversations: LegacyConversationsController;

  private _getCurrentConversationId: () => string;

  private _getCurrentOverview: () => ConversationOverview | null;

  private _selectConversation: (id: string) => void;

  constructor({
    conversations,
    getCurrentConversationId,
    getCurrentOverview,
    selectConversation,
  }: HistoryViewModelOptions) {
    super({
      isOpen: false,
      overviews: [],
      loading: false,
      error: null,
      currentConversationId: getCurrentConversationId(),
    });

    this._conversations = conversations;
    this._getCurrentConversationId = getCurrentConversationId;
    this._getCurrentOverview = getCurrentOverview;
    this._selectConversation = selectConversation;
  }

  open = () => {
    this.setState({
      isOpen: true,
      currentConversationId: this._getCurrentConversationId(),
    });
    this.load();
  };

  close = () => {
    this.setState({
      isOpen: false,
      currentConversationId: this._getCurrentConversationId(),
    });
  };

  back = this.close;

  load = async (
    filters?: ListConversationFilters,
  ): ControllerResult<ConversationOverview[]> => {
    if (this.state.loading) {
      return {
        type: 'success',
        data: this.state.overviews,
      };
    }

    try {
      this.setState({
        loading: true,
        error: null,
        currentConversationId: this._getCurrentConversationId(),
      });

      const response = await this._conversations.list({
        limit: DEFAULT_HISTORY_LIMIT,
        status: 'active',
        ...filters,
      });

      if (response.type === 'error') {
        this.setState({
          loading: false,
          error: response,
        });
        return response;
      }

      const sorted = sortByLastUpdatedDesc(response.data);
      const isActiveHistoryQuery =
        (filters?.status ?? 'active') === 'active' &&
        filters?.conversationIds == null;
      const overviews = isActiveHistoryQuery
        ? this._withPinnedCurrent(sorted)
        : sorted;
      this.setState({
        overviews,
        loading: false,
        error: null,
        currentConversationId: this._getCurrentConversationId(),
      });
      return {
        ...response,
        data: overviews,
      };
    } catch (error) {
      const controllerError = unknownError(error);
      this.setState({
        loading: false,
        error: controllerError,
        currentConversationId: this._getCurrentConversationId(),
      });

      return controllerError;
    }
  };

  // The backend list filter can briefly drop the conversation the user is
  // currently viewing during the live-chat connecting window (it has left
  // the bot conversation but the chat hasn't connected yet). Re-add it here
  // so it doesn't flicker out of the history list on load()/open().
  private _withPinnedCurrent(
    overviews: ConversationOverview[],
  ): ConversationOverview[] {
    const current = this._getCurrentOverview();
    if (
      current == null ||
      current.id !== this._getCurrentConversationId() ||
      isConversationOverviewEnded(current) ||
      overviews.some((o) => o.id === current.id)
    ) {
      return overviews;
    }
    return sortByLastUpdatedDesc([...overviews, current]);
  }

  patchOverview(id: string, patch: Partial<ConversationOverview>): void {
    const index = this.state.overviews.findIndex((o) => o.id === id);
    if (index === -1) return;
    const overviews = [...this.state.overviews];
    overviews[index] = {...overviews[index], ...patch};
    this.setState({overviews});
  }

  syncOverviews(incoming: ConversationOverview[]): void {
    if (incoming.length === 0) return;
    const merged = new Map(this.state.overviews.map((o) => [o.id, o]));
    for (const overview of incoming) {
      merged.set(overview.id, overview);
    }
    this.setState({overviews: sortByLastUpdatedDesc([...merged.values()])});
  }

  selectConversation = (id: string) => {
    const currentConversationId = this._getCurrentConversationId();
    this.setState({
      isOpen: false,
      currentConversationId,
    });

    if (id !== currentConversationId) {
      this._selectConversation(id);
    }
  };
}
